import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorCodeEnum } from '@/config';
import {
  ActivateRiskProfileRequest,
  BaseRequest,
  BaseRiskProfileRequest,
  CreateRiskProfileRequest,
  RiskProfile,
  RiskProfileUpdateRequest,
  RiskProfileUpdateSignalsRequest,
} from '@/api';
import { RiskscoreProfilesData } from '../models';

export interface RiskscoreProfilesSliceState {
  data: RiskscoreProfilesData;
  loading: boolean;
  profileLoading: boolean;
  error: string | null;
  errorCode: ErrorCodeEnum | null;
}

const initialState: RiskscoreProfilesSliceState = {
  data: null,
  loading: false,
  profileLoading: false,
  error: null,
  errorCode: null,
};

const riskscoreProfilesSlice = createSlice({
  name: 'riskscoreProfiles',
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<RiskscoreProfilesData>) => ({
      ...state,
      data: action.payload,
    }),
    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),
    setProfileLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      profileLoading: action.payload,
    }),
    setError: (state, action: PayloadAction<string | null>) => ({
      ...state,
      error: action.payload,
    }),
    setErrorCode: (state, action: PayloadAction<ErrorCodeEnum | null>) => ({
      ...state,
      errorCode: action.payload,
    }),
    updateProfileState: (state, action: PayloadAction<RiskProfile>) => {
      if (state.data === null) {
        return state;
      }

      const currentProfiles = JSON.parse(
        JSON.stringify(state.data?.profiles || []),
      );
      const newProfiles = [...currentProfiles];

      if (action.payload) {
        const updatedProfile = action.payload;
        const updatedProfileIndex = currentProfiles.findIndex(
          (item: RiskProfile) => item.id === updatedProfile.id,
        );

        const currentActiveProfileIndex = currentProfiles.findIndex(
          (item: RiskProfile) => item.is_enabled,
        );

        if (updatedProfileIndex !== -1) {
          newProfiles.splice(updatedProfileIndex, 1, updatedProfile);
        }

        if (
          currentActiveProfileIndex !== updatedProfileIndex &&
          updatedProfile.is_enabled
        ) {
          const prevActiveProfile = {
            ...newProfiles[currentActiveProfileIndex],
            is_enabled: false,
          };
          newProfiles.splice(currentActiveProfileIndex, 1, prevActiveProfile);
        }
      }

      state.data.profiles = newProfiles;
    },
    listRiskProfiles: (state, action: PayloadAction<BaseRequest>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    fetchRiskProfile: (
      state,
      action: PayloadAction<BaseRiskProfileRequest>,
    ) => ({
      ...state,
      error: null,
      profileLoading: true,
    }),
    updateRiskProfile: (
      state,
      action: PayloadAction<RiskProfileUpdateRequest>,
    ) => ({
      ...state,
      error: null,
      profileLoading: true,
    }),
    createRiskProfile: (
      state,
      action: PayloadAction<CreateRiskProfileRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
      errorCode: null,
    }),
    activateRiskProfile: (
      state,
      action: PayloadAction<ActivateRiskProfileRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    setProfileSignals: (
      state,
      action: PayloadAction<RiskProfileUpdateSignalsRequest>,
    ) => ({
      ...state,
      error: null,
      profileLoading: true,
    }),
    resetRiskProfile: (
      state,
      action: PayloadAction<BaseRiskProfileRequest>,
    ) => ({
      ...state,
      error: null,
      profileLoading: true,
    }),
    deleteRiskProfile: (
      state,
      action: PayloadAction<BaseRiskProfileRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
  },
});

export { riskscoreProfilesSlice };
